/**
 * Signed-up patient Firestore updates
 *
 * Writes dot-path patches to `signedUpPatients/{signUpId}` with undefined/NaN stripped
 * so nested `webAppOnboarding` fields can be updated safely from screens.
 */

import {
  SignedUpPatient,
  UserCourseBillingType,
  WebAppOnboardingCurrentStep,
  WebAppOnboardingStateValue,
} from '@physiofit-digital/physiofit_types';
import { doc, FieldValue, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../../firebaseConfig';
import type { InsuranceBillingType } from '../insurance/config';
import type { WebAppOnboardingDisplayedBillingInfo, WebAppOnboardingInsuranceInfo } from '../insurance/metaBuilders';
import { omitFirestoreUndefined } from '../utils/omitFirestoreUndefined';
import { useOnboardingStore } from '../store/onboardingStore';

type DeepPaths<T> = T extends object
  ? {
      [K in keyof T]-?: K extends string | number
        ? `${K}` | `${K}${DeepPaths<T[K]> extends never ? '' : `.${DeepPaths<T[K]>}`}`
        : never;
    }[keyof T]
  : never;

export type PartialDeepPaths<T> = Partial<{ [K in DeepPaths<T>]: unknown }>;

/** Dot-path updates for nested `webAppOnboarding` fields via Firestore `updateDoc`. */
export type WebAppOnboardingFirestorePatch = {
  'webAppOnboarding.currentStep'?: WebAppOnboardingCurrentStep;
  'webAppOnboarding.state'?: WebAppOnboardingStateValue;
  'webAppOnboarding.insuranceInfo'?: WebAppOnboardingInsuranceInfo;
  /**
   * Written by the BNPL data screen (`blocked` for a patient under 18) and by the payment screen (the provider-decline
   * revert and the invoice opt-out).
   */
  'webAppOnboarding.insuranceInfo.billingType'?: UserCourseBillingType;
  /** Written by the BNPL data screen for a patient under 18, together with `billingType: 'blocked'`. */
  'webAppOnboarding.insuranceInfo.blockedReason'?: string;
  'webAppOnboarding.displayedBillingInfo'?: WebAppOnboardingDisplayedBillingInfo | FieldValue;
  'webAppOnboarding.flags.onboardingCompletedDate'?: string;
  'webAppOnboarding.flags.bnplDeclinedDate'?: string;
  'webAppOnboarding.flags.practiceFeeAcknowledgedDate'?: string;
  'webAppOnboarding.signatureStorageUrl'?: string;
  'webAppOnboarding.privateInfo.firstName'?: string;
  'webAppOnboarding.privateInfo.lastName'?: string;
  'webAppOnboarding.privateInfo.email'?: string;
  'webAppOnboarding.privateInfo.birthday'?: string;
  'webAppOnboarding.privateInfo.address.street'?: string;
  'webAppOnboarding.privateInfo.address.houseNumber'?: string;
  'webAppOnboarding.privateInfo.address.zip'?: string;
  'webAppOnboarding.privateInfo.address.city'?: string;
  patientFirstName?: string;
  patientLastName?: string;
  'address.streetName'?: string;
  'address.houseNumber'?: string;
  'address.zipCode'?: string;
  'address.city'?: string;
};

/**
 * Applies a partial update to the current invitation document in Firestore.
 *
 * @param {Object} data Dot-path patch for `SignedUpPatient` / `webAppOnboarding` fields.
 * @return {Promise<void>}
 */
export const editSignedUpPatient = async (data: PartialDeepPaths<SignedUpPatient> | WebAppOnboardingFirestorePatch) => {
  const signUpId = useOnboardingStore.getState().signUpId?.trim();
  if (!signUpId) throw new Error('No signUpId found in store');
  const uid = auth.currentUser?.uid;
  if (!uid || uid !== signUpId) {
    throw new Error('signedUpPatients write requires authenticated signUpId');
  }
  await updateDoc(doc(db, 'signedUpPatients', signUpId), omitFirestoreUndefined(data as Record<string, unknown>));
};

export type { InsuranceBillingType };
